import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Plus, Copy, Mail, Trash2, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

const attestUrl = (token) => `${window.location.origin}/attest/${token}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const copy = async (text) => {
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text); else throw new Error();
    toast.success("Witness link copied");
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    let ok = false; try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(ta);
    ok ? toast.success("Witness link copied") : toast.error("Couldn't copy — please try again.");
  }
};

const mailtoFor = (token) =>
  `mailto:?subject=${encodeURIComponent("Please review my move-in report")}&body=${encodeURIComponent(
    `Hi,\n\nCould you review my move-in condition photos and confirm they match what you saw at the property? It takes a minute — no account needed:\n${attestUrl(token)}\n\nThank you!`
  )}`;

export function WitnessAttestations({ propertyId, property }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const canInvite = property?.is_locked && property?.doc_type === "move_in";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/attestations`);
      setItems(data);
    } catch (e) { console.error("load attestations failed", e); } finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/attestations`);
      await load();
      await copy(attestUrl(data.token));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create invite.");
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/attestations/${id}`); await load(); toast.success("Invite revoked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't revoke."); }
  };

  if (loading) return null;
  if (!canInvite && items.length === 0) return null;

  const pending = items.filter((a) => a.status === "pending");
  const completed = items.filter((a) => a.status === "completed");

  return (
    <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="witness-attestations">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Users size={20} /></span>
          <div>
            <h3 className="font-heading font-semibold text-navy">Witnesses</h3>
            <p className="text-sm text-[#475569]">Invite a roommate, friend or family member to confirm your move-in photos match what they saw.</p>
          </div>
        </div>
        {canInvite && (
          <Button data-testid="add-witness-btn" onClick={create} disabled={creating}
            className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={16} className="mr-1.5" /> Add a witness</>}
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          {completed.map((a) => (
            <div key={a.attestation_id} data-testid={`attestation-completed-${a.attestation_id}`}
              className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
              <p className="text-sm text-emerald-900">
                <span className="font-semibold">{a.witness_name || "Witness"}</span>
                {a.relationship ? `, ${a.relationship}` : ""} — attested {fmtDate(a.attested_at)}
              </p>
            </div>
          ))}
          {pending.map((a) => (
            <div key={a.attestation_id} data-testid={`attestation-pending-${a.attestation_id}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2.5">
              <span className="inline-flex items-center gap-2 text-sm text-[#475569]">
                <Clock size={15} className="text-amber-ink" /> Invite pending
              </span>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" data-testid={`attestation-copy-${a.attestation_id}`} onClick={() => copy(attestUrl(a.token))} className="rounded-lg h-8"><Copy size={14} className="mr-1" /> Copy</Button>
                <a href={mailtoFor(a.token)} data-testid={`attestation-email-${a.attestation_id}`}><Button size="sm" variant="outline" className="rounded-lg h-8"><Mail size={14} className="mr-1" /> Email</Button></a>
                <Button size="sm" variant="ghost" data-testid={`attestation-revoke-${a.attestation_id}`} onClick={() => revoke(a.attestation_id)} className="rounded-lg h-8 text-[#B91C1C] hover:bg-[#FEE2E2]"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
